<script lang="ts">
export default {
  name: 'DateRangeSelector',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { DateTime } from 'luxon';
import { ChevronDown } from '@lucide/vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import AppButton from '@/components/v2/AppButton.vue';
import type { DateChangePayload } from '@/types/components/DateChangePayload';

const props = defineProps<{
  initialStartDate: Date;
  initialEndDate: Date;
  onApply: (payload: DateChangePayload) => void;
}>();

// ─── Popup plumbing ────────────────────────────────────────────────────────

const menuOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const popupRef = ref<HTMLElement | null>(null);
const POPUP_WIDTH = 320; // matches w-80

const popupStyle = ref<{ top: string; left?: string; right?: string } | null>(null);

function updatePopupPosition() {
  const el = triggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const top = rect.bottom + 4;

  // If left-aligned popup would overflow the right edge, anchor to the trigger's right side instead
  if (rect.left + POPUP_WIDTH > viewportWidth) {
    popupStyle.value = {
      top: `${top}px`,
      right: `${viewportWidth - rect.right}px`,
    };
  } else {
    popupStyle.value = {
      top: `${top}px`,
      left: `${rect.left}px`,
    };
  }
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || popupRef.value?.contains(target)) return;
  // DatePickerInput's calendar uses PopoverPortal to teleport to body, so it lives
  // outside popupRef even though it's logically a child of our popup. We check
  // via closest() on the target — this is reliable on mousedown but not on click,
  // because Vue's @click handlers can mutate the DOM (e.g. drill day→month
  // unmounts the title button) before the document listener runs, leaving the
  // event target detached from its ancestors.
  if ((target as Element)?.closest?.('[data-test="dpi-popover"]')) return;
  cancel();
}

let clickOutsideCleanup: (() => void) | null = null;

watch(menuOpen, (open) => {
  if (!open) {
    popupStyle.value = null;
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
    return;
  }
  // Snapshot dates when opening so Cancel can revert
  draftStart.value = startDate.value;
  draftEnd.value = endDate.value;
  nextTick(() => {
    updatePopupPosition();
    const onScroll = (e: Event) => {
      if (popupRef.value?.contains(e.target as Node)) return;
      if ((e.target as Element)?.closest?.('[data-test="dpi-popover"]')) return;
      cancel();
    };
    clickOutsideCleanup = () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', onScroll, true);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', onScroll, true);
  });
});

onUnmounted(() => {
  clickOutsideCleanup?.();
});

function cancel() {
  // Revert uncommitted changes
  newStart.value = props.initialStartDate;
  newEnd.value = props.initialEndDate;
  menuOpen.value = false;
}

function apply() {
  props.onApply({ startDate: startDate.value, endDate: endDate.value });
  menuOpen.value = false;
}

// ─── Date business logic ───────────────────────────────────────────────────

const ALL_TIME_START = DateTime.fromObject({ year: 2019, month: 1, day: 1 }).startOf('day');
const ALL_TIME_END = DateTime.local().plus({ year: 1 }).endOf('day');

const newStart = ref<Date>(props.initialStartDate);
const newEnd = ref<Date>(props.initialEndDate);

// Draft copies used while the menu is open (separate from committed values)
const draftStart = ref<Date>(props.initialStartDate);
const draftEnd = ref<Date>(props.initialEndDate);

watch(() => props.initialStartDate, (v) => { newStart.value = v; });
watch(() => props.initialEndDate, (v) => { newEnd.value = v; });

const startDate = computed(() => newStart.value ?? props.initialStartDate);
const endDate = computed(() => newEnd.value ?? props.initialEndDate);

const startDateFormatted = computed(() =>
  DateTime.fromJSDate(startDate.value).toLocaleString(DateTime.DATE_MED),
);
const endDateFormatted = computed(() =>
  DateTime.fromJSDate(endDate.value).toLocaleString(DateTime.DATE_MED),
);

const isAllTime = computed(() => {
  const s = DateTime.fromJSDate(startDate.value);
  const e = DateTime.fromJSDate(endDate.value);
  return s.hasSame(ALL_TIME_START, 'year') && e.hasSame(ALL_TIME_END, 'year');
});

const quickSelectionClass = computed(() => {
  const s = DateTime.fromJSDate(startDate.value);
  const e = DateTime.fromJSDate(endDate.value);
  const now = DateTime.local();
  if (isAllTime.value) return 'all-time';
  if (e.startOf('week').hasSame(s, 'day')) return 'last-week';
  if (e.startOf('month').hasSame(s, 'day')) return 'last-month';
  if (e.startOf('quarter').hasSame(s, 'day')) return 'last-quarter';
  if (e.hasSame(now, 'day') && s.hasSame(now.startOf('year'), 'day')) return 'year-to-date';
  if (e.startOf('year').hasSame(s, 'day')) return 'last-year';
  if (e.minus({ days: 30 }).hasSame(s, 'day')) return 'trailing-30';
  if (e.minus({ days: 90 }).hasSame(s, 'day')) return 'trailing-90';
  if (e.minus({ days: 180 }).hasSame(s, 'day')) return 'trailing-180';
  if (e.minus({ days: 365 }).hasSame(s, 'day')) return 'trailing-365';
  return '';
});

// ISO strings bound to the date pickers (draft while menu is open)
const draftStartIso = computed({
  get() {
    return DateTime.fromJSDate(draftStart.value).toISODate() ?? '';
  },
  set(val: string) {
    const dt = DateTime.fromISO(val);
    if (!dt.isValid) return;
    const end = DateTime.fromJSDate(draftEnd.value);
    draftStart.value = dt.toJSDate();
    // Keep end ≥ start
    if (end < dt) draftEnd.value = dt.toJSDate();
  },
});

const draftEndIso = computed({
  get() {
    return DateTime.fromJSDate(draftEnd.value).toISODate() ?? '';
  },
  set(val: string) {
    const dt = DateTime.fromISO(val);
    if (!dt.isValid) return;
    const start = DateTime.fromJSDate(draftStart.value);
    draftEnd.value = dt.toJSDate();
    // Keep start ≤ end
    if (start > dt) draftStart.value = dt.toJSDate();
  },
});

// Quick-select helpers — commit immediately and apply
function setRange(start: Date, end: Date) {
  newStart.value = start;
  newEnd.value = end;
  props.onApply({ startDate: start, endDate: end });
  menuOpen.value = false;
}

function setLastWeek() {
  const now = DateTime.local();
  setRange(now.minus({ week: 1 }).startOf('week').toJSDate(), now.minus({ week: 1 }).endOf('week').toJSDate());
}
function setLastMonth() {
  const now = DateTime.local();
  setRange(now.minus({ month: 1 }).startOf('month').toJSDate(), now.minus({ month: 1 }).endOf('month').toJSDate());
}
function setLastQuarter() {
  const now = DateTime.local();
  setRange(now.minus({ quarter: 1 }).startOf('quarter').toJSDate(), now.minus({ quarter: 1 }).endOf('quarter').toJSDate());
}
function setLastYear() {
  const now = DateTime.local();
  setRange(now.minus({ year: 1 }).startOf('year').toJSDate(), now.minus({ year: 1 }).endOf('year').toJSDate());
}
function setYearToDate() {
  const now = DateTime.local();
  setRange(now.startOf('year').toJSDate(), now.endOf('day').toJSDate());
}
function setAllTime() {
  setRange(ALL_TIME_START.toJSDate(), ALL_TIME_END.toJSDate());
}
function setTrailing(days: number) {
  const now = DateTime.local();
  setRange(now.minus({ days: days + 1 }).toJSDate(), now.minus({ days: 1 }).toJSDate());
}

// Apply custom range from date pickers
function applyCustomRange() {
  newStart.value = draftStart.value;
  newEnd.value = draftEnd.value;
  apply();
}
</script>

<template>
  <div class="inline-flex items-center gap-2 py-1 px-3 rounded border border-solid border-gray-200 bg-white">
    <button
      ref="triggerRef"
      type="button"
      class="flex items-center gap-2 cursor-pointer select-none text-base  font-medium text-gray-700 bg-transparent border-none p-0"
      :aria-expanded="menuOpen"
      aria-haspopup="true"
      @click="menuOpen = !menuOpen"
      @keydown.enter.space.prevent="menuOpen = !menuOpen">
      <span v-if="isAllTime">All Time</span>
      <span v-else>{{ startDateFormatted }} – {{ endDateFormatted }}</span>
      <ChevronDown
        :size="16"
        class="text-gray-500 transition-transform duration-150"
        :class="{ 'rotate-180': menuOpen }" />
    </button>

    <Teleport to="body">
      <div
        v-if="menuOpen"
        ref="popupRef"
        class="fixed z-9999 w-80 rounded-md border border-gray-200 bg-white shadow-lg"
        :style="popupStyle ?? undefined"
        @click.stop>
        <!-- Quick selections -->
        <ul :class="quickSelectionClass" class="list-none p-2 m-0">
          <li class="text-xs font-medium uppercase tracking-wide text-gray-400 px-2 pt-2 pb-1">
            Previous Calendar
          </li>
          <li class="quick-item last-week" @click="setLastWeek">Week</li>
          <li class="quick-item last-month" @click="setLastMonth">Month</li>
          <li class="quick-item last-quarter" @click="setLastQuarter">Quarter</li>
          <li class="quick-item last-year" @click="setLastYear">Year</li>
          <li class="text-xs font-medium uppercase tracking-wide text-gray-400 px-2 pt-3 pb-1">
            Trailing Days
          </li>
          <li class="quick-item trailing-30" @click="setTrailing(30)">30 Days</li>
          <li class="quick-item trailing-90" @click="setTrailing(90)">90 Days</li>
          <li class="quick-item trailing-180" @click="setTrailing(180)">180 Days</li>
          <li class="quick-item trailing-365" @click="setTrailing(365)">365 Days</li>
          <li class="text-xs font-medium uppercase tracking-wide text-gray-400 px-2 pt-3 pb-1">
            Other
          </li>
          <li class="quick-item all-time" @click="setAllTime">All Time</li>
          <li class="quick-item year-to-date" @click="setYearToDate">Year-To-Date</li>
        </ul>

        <div class="mx-4 border-b border-gray-200"></div>

        <!-- Custom range -->
        <div class="px-4 pt-3 pb-1">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">Custom Range</p>
          <div class="grid grid-cols-2 gap-3">
            <DatePickerInput
              v-model="draftStartIso"
              label="Start"
              data-test="date-range-start-date-activator" />
            <DatePickerInput
              v-model="draftEndIso"
              label="End"
              data-test="date-range-end-date-activator" />
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 mt-2">
          <AppButton outline @click="cancel" data-test="date-range-cancel-button">Cancel</AppButton>
          <AppButton primary @click="applyCustomRange" data-test="date-range-apply-button">Apply</AppButton>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.quick-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  cursor: pointer;
  color: rgb(55 65 81);
  user-select: none;
}

.quick-item:hover {
  background: rgb(0 0 0 / 0.04);
}

.quick-item::before {
  content: '';
  display: inline-block;
  width: 1rem;
  font-size: 0.75rem;
  color: rgb(79 70 229);
  flex-shrink: 0;
}

/* Active quick-select checkmark */
ul.last-week > li.last-week::before,
ul.last-month > li.last-month::before,
ul.last-quarter > li.last-quarter::before,
ul.last-year > li.last-year::before,
ul.year-to-date > li.year-to-date::before,
ul.trailing-30 > li.trailing-30::before,
ul.trailing-90 > li.trailing-90::before,
ul.trailing-180 > li.trailing-180::before,
ul.trailing-365 > li.trailing-365::before,
ul.all-time > li.all-time::before {
  content: '✓';
}
</style>
